import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  type PreviewOrderApiType,
  type PreviewOrderApiAnotherType,
  getCardList as getCardListApi,
  type CardDataType,
  getPlans as getPlansApi,
  type PlansType,
  type RecommendedType,
} from '@/services/paymenApi';

// Define a type for the slice state
interface PaymentState {
  subscriptionTime: number;
  currency: string;
  productCode: string;
  priviewOrderObj: PreviewOrderApiAnotherType | null;
  cardList: CardDataType[];
  newCard: CardDataType | null;
  planData: {
    myPlans: PlansType[];
  };
  coupon: string;
  trial: number;
  activePaymentMethodId: string;
  previeOrderCheckMethodId: string;
}

// Define the initial state using that type
const initialState: PaymentState = {
  subscriptionTime: 1,
  currency: 'USD',
  productCode: '',
  priviewOrderObj: null,
  cardList: [],
  planData: {
    myPlans: [],
  },
  coupon: '',
  trial: 0,
  newCard: null,
  activePaymentMethodId: '',
  previeOrderCheckMethodId: '',
};

export const getCardList = createAsyncThunk('payment/cards', async (data: {}, { rejectWithValue }) => {
  try {
    const response = await getCardListApi();
    const { errorCode } = response;
    if (errorCode === '0') {
      const data = response.cards;
      return data;
    }
  } catch (err: any) {
    // Use `err.response.data` as `action.payload` for a `rejected` action,
    // by explicitly returning it using the `rejectWithValue()` utility
    return rejectWithValue(err.response.data);
  }
});

export const getPlants = createAsyncThunk(
  'payment/plants',
  async (param: { lang: string; referral?: string }, { rejectWithValue }) => {
    try {
      const { lang, referral } = param;
      const response = await getPlansApi(lang, referral);
      const { errorCode } = response;
      if (errorCode === '0') {
        const data = response.data;
        return data;
      }
    } catch (err: any) {
      // Use `err.response.data` as `action.payload` for a `rejected` action,
      // by explicitly returning it using the `rejectWithValue()` utility
      return rejectWithValue(err.response.data);
    }
  },
);

export const paymentSlice = createSlice({
  name: 'payment',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setCurrency: (state, action: PayloadAction<string>) => {
      state.currency = action.payload;
    },
    setSubscriptionTime: (state, action: PayloadAction<number>) => {
      state.subscriptionTime = action.payload;
    },
    setProductCode: (state, action: PayloadAction<string>) => {
      state.productCode = action.payload;
    },
    setPriviewOrderObj: (state, action: PayloadAction<PreviewOrderApiAnotherType>) => {
      state.priviewOrderObj = action.payload;
    },
    setCoupon: (state, action: PayloadAction<string>) => {
      state.coupon = action.payload;
    },
    setTrial: (state, action: PayloadAction<number>) => {
      state.trial = action.payload;
    },
    setNewCard: (state, action: PayloadAction<CardDataType | null>) => {
      state.newCard = action.payload;
    },
    setActivePaymentMethodId: (state, action: PayloadAction<string>) => {
      state.activePaymentMethodId = action.payload;
    },
    setPrevieOrderCheckMethodId: (state, action: PayloadAction<string>) => {
      state.previeOrderCheckMethodId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder
      .addCase(getCardList.fulfilled, (state, action) => {
        // Add user to the state array
        if (action.payload) state.cardList = action.payload;
      })
      .addCase(getPlants.fulfilled, (state, action) => {
        // Add user to the state array
        if (action.payload) state.planData = action.payload;
      });
  },
});

export const {
  setCurrency,
  setProductCode,
  setSubscriptionTime,
  setPriviewOrderObj,
  setCoupon,
  setTrial,
  setNewCard,
  setActivePaymentMethodId,
  setPrevieOrderCheckMethodId,
} = paymentSlice.actions;

export default paymentSlice.reducer;
